import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { reportError } from '@/lib/error-reporter'

interface Props {
  children: ReactNode
  fallback?: (props: { error: Error; reset: () => void }) => ReactNode
  /** Free-form tag added to the reporter envelope so operators can
   *  bucket errors by feature in the tracker. e.g. "feature=cases". */
  scope?: string
}

interface State {
  error: Error | null
}

/**
 * App-wide error boundary used at the root layout. Per-route boundaries can
 * be added inside features when partial failures should not blow up the shell.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      componentStack: info.componentStack ?? undefined,
      tags: this.props.scope ? { scope: this.props.scope } : undefined,
    })
  }

  reset = (): void => this.setState({ error: null })

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset })
    }

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="max-w-md text-sm text-muted-fg">{error.message}</p>
        <Button onClick={this.reset}>Try again</Button>
      </div>
    )
  }
}
